// src/components/payout/BankForm.jsx
import React, { useState, useCallback, memo, useEffect } from 'react';
import { Search, ChevronRight, CheckCircle, AlertTriangle, Info } from 'lucide-react';

const BankForm = memo(({
  newMethod,
  banks,
  loadingBanks,
  formErrors,
  verifyingAccount,
  accountVerificationResult,
  nameVerificationStatus,
  onInputChange,
  onBankSelect,
  onAddMethod,
  processing,
  onCancel
}) => {
  const [bankSearchTerm, setBankSearchTerm] = useState('');
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [filteredBanks, setFilteredBanks] = useState([]);

  // Filter banks based on search term
  useEffect(() => {
    if (!bankSearchTerm) {
      setFilteredBanks(banks);
      return;
    }
    
    const filtered = banks.filter(bank =>
      bank.name.toLowerCase().includes(bankSearchTerm.toLowerCase()) ||
      bank.code.includes(bankSearchTerm)
    );
    setFilteredBanks(filtered);
  }, [banks, bankSearchTerm]);

  const handleFocus = useCallback((fieldName) => {
    setFocusedField(fieldName);
  }, []);

  const handleBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  const handleBankDropdownToggle = useCallback(() => {
    setShowBankDropdown(prev => !prev);
  }, []);

  const handleBankSearchChange = useCallback((e) => {
    setBankSearchTerm(e.target.value);
  }, []);

  const handleSelectBank = useCallback((bank) => {
    onBankSelect(bank);
    setShowBankDropdown(false);
    setBankSearchTerm('');
  }, [onBankSelect]);

  const handleLocalInputChange = useCallback((e) => {
    onInputChange(e);
    if (formErrors[e.target.name]) {
      // Clear error for this field
      onInputChange({
        target: { name: 'clearError', value: e.target.name }
      });
    }
  }, [onInputChange, formErrors]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    onAddMethod();
  }, [onAddMethod]);

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Account Verification Status */}
      {accountVerificationResult && (
        <div className={`p-4 rounded-lg ${
          nameVerificationStatus?.match ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
        }`}>
          <div className="flex items-start gap-3">
            {nameVerificationStatus?.match ? (
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1">
              <p className="font-medium">
                {nameVerificationStatus?.match ? 'Account Verified' : 'Verification Notice'}
              </p>
              <p className="text-sm mt-1">
                Bank Name: <span className="font-medium">{accountVerificationResult.verified_account_name}</span>
              </p>
              {nameVerificationStatus?.auto_updated && (
                <p className="text-sm text-green-700 mt-1">
                  ✓ Your profile has been updated to match the bank name
                </p>
              )}
              {!nameVerificationStatus?.match && !nameVerificationStatus?.auto_updated && (
                <p className="text-sm text-yellow-700 mt-1">
                  Name doesn't match profile. Your profile will be updated automatically.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Form Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Bank Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bank Name *
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={handleBankDropdownToggle}
              className={`w-full p-3 border rounded-lg text-left flex justify-between items-center transition-all ${
                formErrors.bank_name ? 'border-red-300' : 'border-gray-300'
              } ${loadingBanks ? 'bg-gray-50' : 'bg-white'} ${
                focusedField === 'bank_name' ? 'border-[#ff660e] ring-1 ring-[#ff660e]' : ''
              }`}
              disabled={loadingBanks}
              onFocus={() => handleFocus('bank_name')}
              onBlur={handleBlur}
            >
              {newMethod.bank_name || (
                loadingBanks ? 'Loading banks...' : 'Select a bank'
              )}
              <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${
                showBankDropdown ? 'rotate-90' : ''
              }`} />
            </button>
            
            {showBankDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {/* Search Input */}
                <div className="sticky top-0 p-2 bg-white border-b">
                  <div className="relative">
                    <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search for your bank..."
                      value={bankSearchTerm}
                      onChange={handleBankSearchChange}
                      className="w-full pl-10 pr-3 py-2 text-sm border rounded"
                      autoFocus
                    />
                  </div>
                </div>
                
                {/* Bank List */}
                <div className="py-1">
                  {filteredBanks.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500 text-center">
                      {loadingBanks ? 'Loading...' : 'No banks found'}
                    </div>
                  ) : (
                    filteredBanks.map((bank) => (
                      <button
                        key={`bank-${bank.id || bank.code}`}
                        type="button"
                        onClick={() => handleSelectBank(bank)}
                        className="w-full px-3 py-2 text-sm text-left hover:bg-gray-50 flex items-center justify-between"
                      >
                        <span>{bank.name}</span>
                        <span className="text-xs text-gray-500 font-mono">{bank.code}</span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          {formErrors.bank_name && (
            <p className="mt-1 text-sm text-red-600">{formErrors.bank_name}</p>
          )}
        </div>

        {/* Account Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Account Number *
          </label>
          <div className="relative">
            <input
              type="text"
              name="account_number"
              value={newMethod.account_number}
              onChange={handleLocalInputChange}
              onFocus={() => handleFocus('account_number')}
              onBlur={handleBlur}
              placeholder="10-digit account number"
              maxLength="10"
              className={`w-full p-3 border rounded-lg transition-all ${
                formErrors.account_number ? 'border-red-300' : 'border-gray-300'
              } ${focusedField === 'account_number' ? 'border-[#ff660e] ring-1 ring-[#ff660e]' : ''}`}
            />
            {verifyingAccount && (
              <div className="absolute right-3 top-3">
                <div className="w-4 h-4 border-2 border-[#ff660e] border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
          </div>
          {formErrors.account_number && (
            <p className="mt-1 text-sm text-red-600">{formErrors.account_number}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Must be a 10-digit Nigerian account number
          </p>
        </div>

        {/* Account Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Account Name *
          </label>
          <input
            type="text"
            name="account_name"
            value={newMethod.account_name}
            onChange={handleLocalInputChange}
            onFocus={() => handleFocus('account_name')}
            onBlur={handleBlur}
            placeholder="Will be filled automatically"
            readOnly={!!accountVerificationResult}
            className={`w-full p-3 border rounded-lg transition-all ${
              accountVerificationResult ? 'bg-gray-50' : formErrors.account_name ? 'border-red-300' : 'border-gray-300'
            } ${focusedField === 'account_name' ? 'border-[#ff660e] ring-1 ring-[#ff660e]' : ''}`}
          />
          {formErrors.account_name && (
            <p className="mt-1 text-sm text-red-600">{formErrors.account_name}</p>
          )}
          {accountVerificationResult && (
            <p className="mt-1 text-sm text-green-600">
              ✓ Verified by bank
            </p>
          )}
        </div>

        {/* Bank Code */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bank Code
          </label>
          <input
            type="text"
            name="bank_code"
            value={newMethod.bank_code}
            readOnly
            className="w-full p-3 border rounded-lg bg-gray-50"
          />
          {formErrors.bank_code && (
            <p className="mt-1 text-sm text-red-600">{formErrors.bank_code}</p>
          )}
        </div>
      </div>

      {/* Set as Default */}
      <div className="p-4 border rounded-lg bg-gray-50">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="is_default"
            name="is_default"
            checked={newMethod.is_default}
            onChange={handleLocalInputChange}
            className="w-5 h-5 rounded"
            style={{ accentColor: '#ff660e' }}
          />
          <label htmlFor="is_default" className="text-sm text-gray-700 font-medium">
            Set as default payout method
          </label>
        </div>
        <p className="text-xs text-gray-600 mt-2 ml-8">
          This account will be used as the default for all withdrawals
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 pt-6 border-t">
        <button
          type="submit"
          disabled={processing || verifyingAccount || !accountVerificationResult}
          className="flex-1 bg-[#ff660e] text-white py-3.5 rounded-lg font-semibold hover:bg-[#e55a0d] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {processing ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Adding Bank Account...
            </>
          ) : (
            'Add Bank Account'
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={processing}
          className="px-8 py-3.5 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
      </div>

      {/* Verification Info */}
      <div className="p-4 bg-blue-50 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-blue-800 mb-2">How Bank Verification Works</h4>
            <ul className="text-sm text-blue-700 space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>We verify your account number with your bank using Paystack</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>The account name is fetched automatically - no need to type it</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>If the bank name doesn't match your profile, we'll update it automatically</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span>All future bank accounts must match your verified profile name</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </form>
  );
});

export default BankForm;